<script>
import { onMounted, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useModalStore } from '@/stores/modal.js'
import { createUserAddress, indexUserAddresses, updateUserAddress } from '@/lib/api/user_addresses.js'
import { useGeocodeStore } from '@/stores/geocode.js'
import { useToastStore } from '@/stores/toast.js'
import SkeletonLoader from '@/components/ui/skeleton_loader.vue'

export default {
  components: { SkeletonLoader },
  setup() {
    // I18n
    const { t } = useI18n()

    // Variables
    const userAddressesLoading = ref(true)
    const userAddresses = ref([])
    const showSuggestedLocation = ref(false)
    const suggestedLocation = ref(null)
    const emptyUserAddress = ref({
      address: '',
      address_2: '',
      city: '',
      state_abr: '',
      us_state_id: t('modal.add_address.state_placeholder'),
      country_id: null,
      primary: false,
      nickname: '',
      zip: ''
    })

    // Stores
    const authenticationStore = useAuthenticationStore()
    const modalStore = useModalStore()
    const geocodeStore = useGeocodeStore()
    const toastStore = useToastStore()

    onMounted(async () => {
      await getUserAddresses()
      checkForSuggestedLocation()
      
      // Listen for address saved events
      window.addEventListener('addressSaved', async () => {
        await getUserAddresses()
        checkForSuggestedLocation()
      })
    })

    const getUserAddresses = async () => {
      if (!authenticationStore.currentUser?.user?.id) {
        userAddressesLoading.value = false
        return
      }
      
      let params = new URLSearchParams()
      params.set('query[user_id_eq]', authenticationStore.currentUser.user.id)
      params.set('query[archived_eq]', 'false')
      params.set('query[is_ephemeral_eq]', 'false')

      await indexUserAddresses(params)
        .then((response) => {
          userAddresses.value = response.map((address) => {
            return {
              id: address.id,
              address: address.address,
              address_2: address.address_2,
              city: address.city,
              state_abr: address.us_state.abbreviation,
              us_state: address.us_state,
              us_state_id: address.us_state.id,
              country_id: address.country_id,
              primary: address.primary,
              nickname: address.nickname,
              zip: address.zip,
            }
          })
          userAddressesLoading.value = false
        })
    }

    const isLocationNearExisting = (location) => {
      return userAddresses.value.some(addr => {
        if (!addr.latitude || !addr.longitude) {
          return addr.city.toLowerCase() === location.city.toLowerCase() && 
                 addr.state_abr.toLowerCase() === location.state.toLowerCase()
        }
        const R = 6371
        const dLat = (addr.latitude - location.latitude) * Math.PI / 180
        const dLon = (addr.longitude - location.longitude) * Math.PI / 180
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(location.latitude * Math.PI / 180) * Math.cos(addr.latitude * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2)
        const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
        return distance < 1
      })
    }

    const checkForSuggestedLocation = () => {
      const location = geocodeStore.userLocation
      
      if (location && location.address && location.city && location.state && location.latitude && location.longitude) {
        const isNearExisting = isLocationNearExisting(location)
        
        if (!isNearExisting && !localStorage.getItem('denied_location_suggestion')) {
          suggestedLocation.value = {
            address: location.address,
            city: location.city,
            state_abr: location.state,
            nickname: location.nickname || `${location.city}, ${location.state}`,
            zip: ''
          }
          showSuggestedLocation.value = true
        }
      } else {
        showSuggestedLocation.value = false
      }
    }

    const acceptSuggestedLocation = () => {
      modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, suggestedLocation.value)
      showSuggestedLocation.value = false
    }

    const denySuggestedLocation = () => {
      localStorage.setItem('denied_location_suggestion', 'true')
      showSuggestedLocation.value = false
    }

    const isRefreshing = ref(false)
    
    const shouldShowLocationSuggestion = computed(() => {
      const location = geocodeStore.userLocation
      if (!location || !location.latitude || !location.longitude) return true
      return !isLocationNearExisting(location)
    })

    const refreshLocation = async () => {
      isRefreshing.value = true
      geocodeStore.$reset()
      localStorage.removeItem('denied_location_suggestion')
      
      // Show toast notification
      try {
        const { useToastStore } = await import('@/stores/toast.js');
        const toastStore = useToastStore();
        toastStore.success(t('toast.location_refreshed'));
      } catch (e) {
        console.log('Toast notification failed:', e);
      }
      
      setTimeout(() => {
        checkForSuggestedLocation()
        isRefreshing.value = false
      }, 1000)
    }

    const suggestCurrentLocation = () => {
      modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, emptyUserAddress.value)
    }

    const createAddress = async () => {
      if (!authenticationStore.currentUser?.user?.id) {
        return
      }
      
      if (!modalStore.newUserAddress.address || !modalStore.newUserAddress.city || !modalStore.newUserAddress.us_state_id || modalStore.newUserAddress.us_state_id === t('modal.add_address.state_placeholder')) {
        modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, {...modalStore.newUserAddress, error: {type: 'validation_error'}})
        return
      }
      
      modalStore.newUserAddress.user_id = authenticationStore.currentUser.user.id

      try {
        const location = (await geocodeStore.fetchAddressLocation(modalStore.newUserAddress))[0]
        modalStore.newUserAddress.latitude = location.lat
        modalStore.newUserAddress.longitude = location.lon

        await createUserAddress(modalStore.newUserAddress)
          .then(async () => {
            await getUserAddresses()
            checkForSuggestedLocation()
            modalStore.resetModal()
            toastStore.success(t('settings.saved_location_section.location_saved_success'))
          })
      } catch (error) {
        modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, {...modalStore.newUserAddress, error: {type: 'geocoding_error'}})
      }
    }

    const updatePrimaryAddress = async (address) => {
      await updateUserAddress(address.id, {primary: true})
        .then(() => {
          getUserAddresses()
        })
    }

    const updateAddressArchived = async (address) => {
      await updateUserAddress(address.id, { archived: true })
        .then(() => {
          getUserAddresses()
        })
    }

    const updateAddress = async () => {
      if (!authenticationStore.currentUser?.user?.id) {
        return
      }
      
      if (!modalStore.newUserAddress.address || !modalStore.newUserAddress.city || !modalStore.newUserAddress.us_state_id || modalStore.newUserAddress.us_state_id === t('modal.add_address.state_placeholder')) {
        modalStore.setModal(modalStore.modalTypeConstants.EDIT_ADDRESS, {...modalStore.newUserAddress, error: {type: 'validation_error'}})
        return
      }
      
      modalStore.newUserAddress.user_id = authenticationStore.currentUser.user.id

      try {
        const location = (await geocodeStore.fetchAddressLocation(modalStore.newUserAddress))[0]
        modalStore.newUserAddress.latitude = location.lat
        modalStore.newUserAddress.longitude = location.lon

        await updateUserAddress(modalStore.newUserAddress.id, modalStore.newUserAddress)
          .then(() => {
            getUserAddresses()
            modalStore.resetModal()
            toastStore.success(t('settings.saved_location_section.location_saved_success'))
          })
      } catch (error) {
        modalStore.setModal(modalStore.modalTypeConstants.EDIT_ADDRESS, {...modalStore.newUserAddress, error: {type: 'geocoding_error'}})
      }
    }

    watch(() => geocodeStore.userLocation, () => {
      checkForSuggestedLocation()
    })

    return {
      t,
      userAddresses,
      userAddressesLoading,
      showSuggestedLocation,
      suggestedLocation,
      emptyUserAddress,
      modalStore,
      geocodeStore,
      updatePrimaryAddress,
      updateAddressArchived,
      suggestCurrentLocation,
      acceptSuggestedLocation,
      denySuggestedLocation,
      refreshLocation,
      isRefreshing,
      shouldShowLocationSuggestion
    }
  }
}
</script>

<template>
  <div class="max-w-6xl mx-auto p-6">
    <!-- Page Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ t('settings.saved_locations') }}</h1>
      <p class="text-gray-600">{{ t('settings.saved_location_section.manage_addresses') }}</p>
    </div>

    <!-- Location Suggestion Card -->
    <div v-if="showSuggestedLocation" class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
      <div class="flex items-start justify-between">
        <div class="flex items-start gap-4">
          <div class="p-2 bg-blue-100 rounded-lg">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('settings.saved_location_section.we_found_location') }}</h3>
            <p class="text-gray-700 mb-1">{{ suggestedLocation?.address }}</p>
            <p class="text-gray-600 text-sm">{{ suggestedLocation?.city }}, {{ suggestedLocation?.state_abr }}</p>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            @click="acceptSuggestedLocation()"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm">
            {{ t('settings.saved_location_section.save') }}
          </button>
          <button
            @click="denySuggestedLocation()"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium text-sm">
            {{ t('settings.saved_location_section.dismiss') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Saved Locations Section -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-1">{{ t('settings.saved_location_section.saved_locations') }}</h2>
            <p class="text-sm text-gray-600">{{ t('settings.saved_location_section.frequently_used_addresses') }}</p>
          </div>
          <div class="flex gap-2">
            <button
              v-if="shouldShowLocationSuggestion"
              @click="refreshLocation()"
              :disabled="isRefreshing"
              class="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm disabled:opacity-50">
              <svg :class="{'animate-spin': isRefreshing}" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <span>{{ isRefreshing ? t('settings.saved_location_section.refreshing') : t('settings.saved_location_section.refresh') }}</span>
            </button>
            <button
              @click="suggestCurrentLocation()"
              class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              <span>{{ t('settings.saved_location_section.add_location') }}</span>
            </button>
          </div>
        </div>
      </div>
      
      <div class="p-6">
        <div v-if="userAddressesLoading">
          <SkeletonLoader class="h-20 mb-4" />
          <SkeletonLoader class="h-20 mb-4" />
          <SkeletonLoader class="h-20" />
        </div>
        
        <div v-else-if="userAddresses.length === 0" class="text-center py-12">
          <div class="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('settings.saved_location_section.no_saved_locations') }}</h3>
          <p class="text-gray-600 mb-4">{{ t('settings.saved_location_section.add_first_location') }}</p>
          <button
            @click="suggestCurrentLocation()"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
            {{ t('settings.saved_location_section.add_first_location_button') }}
          </button>
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="address in userAddresses"
            :key="address.id"
            class="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
            
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <h3 class="font-semibold text-gray-900">{{ address.nickname || t('settings.saved_location_section.unnamed_location') }}</h3>
                  <span v-if="address.primary" class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                    {{ t('settings.saved_location_section.primary') }}
                  </span>
                </div>
                <div class="text-sm text-gray-600 space-y-1">
                  <p>{{ address.address }}</p>
                  <p v-if="address.address_2">{{ address.address_2 }}</p>
                  <p>{{ address.city }}, {{ address.state_abr }} {{ address.zip }}</p>
                </div>
              </div>
              
              <div class="flex items-center gap-2 ml-4">
                <button
                  v-if="!address.primary"
                  @click="updatePrimaryAddress(address)"
                  class="px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors text-sm">
                  {{ t('settings.saved_location_section.make_primary') }}
                </button>
                <button
                  @click="modalStore.setModal(modalStore.modalTypeConstants.EDIT_ADDRESS, address)"
                  class="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm">
                  {{ t('settings.saved_location_section.edit') }}
                </button>
                <button
                  @click="updateAddressArchived(address)"
                  class="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm">
                  {{ t('settings.saved_location_section.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/settings/personal_information';
</style>